import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent, ResponsiveFooter, ResponsiveHeader, Card, Modal } from '../ui';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { DocumentsIcon, HealthcareDirectiveIcon, HealthcarePowerOfAttorneyIcon, LastWillIcon, LivingTrustIcon, OtherLegalDocumentsIcon } from '../ui/Icons';
import FileUpload from '../ui/FileUpload';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

interface LegalDocument {
  id: string;
  type: 'will' | 'trust' | 'healthcare_power_of_attorney' | 'healthcare_directive' | 'other';
  name: string;
  description?: string;
  uploadDate: string;
  lastModified?: string;
  notes?: string;
  documents: AppDocument[];
}

const DOCUMENT_TYPES = [
  {
    key: 'will',
    label: 'Last Will and Testament',
    description: 'Your most recent will detailing asset distribution',
    Icon: LastWillIcon
  },
  {
    key: 'trust',
    label: 'Living Trust',
    description: 'Legal documents that specify how your assets are held, managed, and distributed by your chosen trustee.',
    Icon: LivingTrustIcon
  },
  {
    key: 'healthcare_power_of_attorney',
    label: 'Healthcare Power of Attorney',
    description: 'Legal authority for healthcare decisions',
    Icon: HealthcarePowerOfAttorneyIcon
  },
  {
    key: 'healthcare_directive',
    label: 'Healthcare Directive',
    description: 'Your healthcare wishes and end-of-life directives',
    Icon: HealthcareDirectiveIcon
  },
  {
    key: 'other',
    label: 'Other Legal Documents',
    description: 'Additional legal documents',
    Icon: OtherLegalDocumentsIcon
  }
] as const;

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const Frame214: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress, completeTask } = useSessionPersistence();
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [selectedType, setSelectedType] = useState<typeof DOCUMENT_TYPES[number]['key'] | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<AppDocument[]>([]);
  const [notes, setNotes] = useState('');
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

  useEffect(() => {
    const initSession = async () => {
      try {
        if (!session?.taskProgress?.legal_documents) {
          await updateTaskProgress('legal_documents', { documents: [] });
          setDocuments([]);
        } else {
          const savedDocs = session.taskProgress.legal_documents.answers?.documents || [];
          console.log('[Frame214] Loading legal documents from session:', savedDocs);
          setDocuments(
            savedDocs.map((doc: LegalDocument) => ({
              ...doc,
              documents: normalizeDocuments(doc.documents || []),
            }))
          );
        }
      } catch (error) {
        console.error('[Frame214] Failed to initialize session:', error);
      }
    };

    initSession();
  }, [session?.taskProgress?.legal_documents, updateTaskProgress]);

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame214] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const handleUploadDocuments = async () => {
    if (uploadedFiles.length === 0 || !selectedType) return;

    const newDocs: LegalDocument[] = uploadedFiles.map((file) => ({
      id: `${Date.now()}-${file.id}`,
      type: selectedType,
      name: file.name,
      description: notes,
      uploadDate: file.createdAt || new Date().toISOString(),
      lastModified: file.createdAt || new Date().toISOString(),
      notes,
      documents: [file],
    }));

    const updatedDocs = [...documents, ...newDocs];
    setDocuments(updatedDocs);

    await updateTaskProgress('legal_documents', { documents: updatedDocs });

    setShowUploadModal(false);
    setUploadedFiles([]);
    setNotes('');
    setSelectedType(null);
    setIsUploadingDocuments(false);
  };

  const handleEditDocument = (docId: string) => {
    const doc = documents.find(d => d.id === docId);
    if (doc) {
      setEditingDocumentId(docId);
      setEditNotes(doc.notes || '');
      setShowEditModal(true);
    }
  };

  const handleUpdateDocumentNotes = async () => {
    if (!editingDocumentId) return;

    const updatedDocs = documents.map(doc =>
      doc.id === editingDocumentId
        ? { ...doc, notes: editNotes.trim(), description: editNotes.trim() || undefined }
        : doc
    );
    setDocuments(updatedDocs);

    await updateTaskProgress('legal_documents', { documents: updatedDocs });

    setShowEditModal(false);
    setEditingDocumentId(null);
    setEditNotes('');
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    const docToDelete = documents.find(doc => doc.id === docId);
    const attachedDocs = docToDelete?.documents || [];

    for (const doc of attachedDocs) {
      try {
        await documentService.delete(doc.id);
      } catch (error) {
        console.error('[Frame214] Failed to delete document while deleting legal document:', error);
      }
    }

    const updatedDocs = documents.filter(doc => doc.id !== docId);
    setDocuments(updatedDocs);

    await updateTaskProgress('legal_documents', { documents: updatedDocs });
  };

  const handleRemoveAttachedDocument = async (legalDocId: string, documentIndex: number) => {
    const legalDoc = documents.find(doc => doc.id === legalDocId);
    const docToRemove = legalDoc?.documents?.[documentIndex];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame214] Failed to delete saved document:', error);
      alert('Failed to delete document.');
      return;
    }

    const updatedDocs = documents.map((doc) => {
      if (doc.id !== legalDocId) return doc;
      return {
        ...doc,
        documents: normalizeDocuments(doc.documents || []).filter((_, index) => index !== documentIndex),
      };
    });

    setDocuments(updatedDocs);
    await updateTaskProgress('legal_documents', { documents: updatedDocs });
  };

  const removeDocumentFromUploadModal = async (index: number) => {
    const docToRemove = uploadedFiles[index];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame214] Failed to delete unsaved document:', error);
      alert('Failed to delete document.');
      return;
    }

    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNext = async () => {
    if (documents.length > 0) {
      await completeTask('legal_documents');
    }
    navigate('/frame-261');
  };

  const handleBack = () => {
    navigate('/frame-260');
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader
          title="Upload Important Legal Documents"
        />

        <div className="space-y-6">
          <div className="max-w-4xl">
            <p className="text-stone-800 text-lg font-normal font-['Work_Sans'] leading-relaxed mb-6">
              Upload your important legal documents to ensure your loved ones have access to them when needed.
              These documents will be securely stored and only accessible to your designated executors.
            </p>
            <div className="bg-stone-100 rounded-lg p-6 mb-8">
              <h2 className="text-lg font-semibold text-stone-800 mb-4">Common Legal Documents</h2>
              <ul className="list-disc list-inside space-y-2 text-stone-700">
                <li>Last Will and Testament</li>
                <li>Power of Attorney (Financial and Medical)</li>
                <li>Living Will / Healthcare Directive</li>
                <li>Life Insurance Policies</li>
                <li>Property Deeds and Titles</li>
                <li>Marriage/Divorce Certificates</li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {DOCUMENT_TYPES.map(type => (
              <button
                key={type.key}
                onClick={() => {
                  setSelectedType(type.key);
                  setShowUploadModal(true);
                }}
                className="p-6 bg-white rounded-lg border border-stone-200 hover:border-cyan-500 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <type.Icon className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-stone-800 leading-tight">{type.label}</h3>
                    <p className="text-sm text-stone-600 mt-1">{type.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {documents.length > 0 && (
            <Card>
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-stone-800">Uploaded Documents</h3>
              </div>
              <div className="space-y-3">
                {documents.map(doc => {
                  const getDocumentIcon = () => {
                    if (doc.type === 'will') return LastWillIcon;
                    if (doc.type === 'trust') return LivingTrustIcon;
                    if (doc.type === 'healthcare_power_of_attorney') return HealthcarePowerOfAttorneyIcon;
                    if (doc.type === 'healthcare_directive') return HealthcareDirectiveIcon;
                    return OtherLegalDocumentsIcon;
                  };
                  const DocumentIcon = getDocumentIcon();
                  const attachedDocs = normalizeDocuments(doc.documents || []);

                  return (
                    <div key={doc.id} className="bg-stone-50 rounded-lg p-3">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex flex-col gap-2 min-w-0 flex-1 sm:flex-row sm:items-start sm:gap-3">
                          <DocumentIcon className="w-5 h-5 text-stone-600 flex-shrink-0" />

                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-stone-800 truncate">{doc.name}</h4>
                            <p className="text-sm text-stone-600 break-words">
                              {DOCUMENT_TYPES.find(t => t.key === doc.type)?.label}
                            </p>
                            {doc.notes && (
                              <p className="text-sm text-stone-500 mt-1 break-words">{doc.notes}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:shrink-0">
                          <button
                            onClick={() => handleEditDocument(doc.id)}
                            className="text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded border border-cyan-200 hover:border-cyan-300"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="text-red-600 hover:text-red-700 px-3 py-1 rounded border border-red-200 hover:border-red-300"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      <div className="text-xs text-stone-500 mt-2">
                        Uploaded {new Date(doc.uploadDate).toLocaleDateString()}
                      </div>

                      {attachedDocs.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {attachedDocs.map((attachedDoc, index) => (
                            <div
                              key={`${doc.id}-${attachedDoc.id}-${index}`}
                              className="flex flex-col gap-3 rounded-md border border-stone-200 bg-white px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <button
                                type="button"
                                onClick={() => openDocument(attachedDoc)}
                                className="flex items-start gap-2 min-w-0 w-full text-left sm:flex-1"
                              >
                                <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                                <span
                                  className="text-sm font-medium text-stone-800 truncate"
                                  title={attachedDoc.name}
                                >
                                  {attachedDoc.name}
                                </span>
                              </button>

                              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:shrink-0">
                                <button
                                  type="button"
                                  onClick={() => downloadDocument(attachedDoc)}
                                  className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                                >
                                  Download
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleRemoveAttachedDocument(doc.id, index)}
                                  className="text-red-600 hover:text-red-700 text-xs font-medium"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          <Card className="bg-stone-100 border-stone-200">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-cyan-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-medium text-stone-800">Need Help?</h4>
                <p className="text-sm text-stone-600 mt-3">
                  Having these documents readily available ensures your wishes are known and can be carried out.
                  Consider working with a legal professional to create or update any missing documents.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Complete Your Legal Essentials"
        subtitle="Upload your important legal documents"
        progressPercent={33}
        onNext={handleNext}
        onBack={handleBack}
        nextText="Next"
        backText="Back"
      />

      {showUploadModal && selectedType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <h2 className="text-xl font-semibold mb-4">
              Upload {DOCUMENT_TYPES.find(t => t.key === selectedType)?.label}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Select Document
                </label>
                <FileUpload
                  onUpload={async (files) => {
                    if (!files.length) return;

                    setIsUploadingDocuments(true);

                    try {
                      const uploadedDocs = await Promise.all(
                        files.map((file) => documentService.upload(file))
                      );
                      setUploadedFiles(uploadedDocs);
                    } catch (error) {
                      console.error('[Frame214] Failed to upload document(s):', error);
                      alert('Failed to upload one or more documents.');
                    } finally {
                      setIsUploadingDocuments(false);
                    }
                  }}
                  accept=".pdf,.doc,.docx,.txt"
                  multiple={false}
                />

                {isUploadingDocuments && (
                  <div className="mt-3 text-sm text-stone-500">
                    Uploading documents...
                  </div>
                )}

                {uploadedFiles.length > 0 && (
                  <ul className="mt-2 space-y-2">
                    {uploadedFiles.map((file, index) => (
                      <li
                        key={`${file.id}-${index}`}
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm text-stone-600 bg-stone-50 p-2 border border-stone-200 rounded"
                      >
                        <button
                          type="button"
                          onClick={() => openDocument(file)}
                          className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
                        >
                          <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                          <span className="break-words whitespace-normal">{file.name}</span>
                        </button>

                        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                          <button
                            type="button"
                            onClick={() => downloadDocument(file)}
                            className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                          >
                            Download
                          </button>

                          <button
                            type="button"
                            onClick={() => removeDocumentFromUploadModal(index)}
                            className="text-red-600 hover:text-red-700 text-xs font-medium"
                          >
                            Remove
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full border border-stone-300 rounded-md px-3 py-2 h-24"
                  placeholder="Add any notes about this document..."
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadedFiles([]);
                    setNotes('');
                    setIsUploadingDocuments(false);
                  }}
                  className="px-4 py-2 text-stone-700 hover:text-stone-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadDocuments}
                  disabled={uploadedFiles.length === 0}
                  className="px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700 disabled:bg-stone-300 disabled:cursor-not-allowed"
                >
                  Upload
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={showEditModal}
        onClose={() => { setShowEditModal(false); setEditingDocumentId(null); setEditNotes(''); }}
        title="Edit Document Notes"
        okText="Save Notes"
        cancelText="Cancel"
        onOk={handleUpdateDocumentNotes}
        onCancel={() => { setShowEditModal(false); setEditingDocumentId(null); setEditNotes(''); }}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Notes
            </label>
            <textarea
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              className="w-full border border-stone-300 rounded-md px-3 py-2 h-24"
              placeholder="Add any notes about this document..."
            />
          </div>
        </div>
      </Modal>

    </ResponsiveContainer>
  );
};

export default Frame214;